/**
 * @file: chatNotificationText.ts
 *
 * Localized rendering for server-issued chat system notifications
 * (`members.invited`, `room.dismissed`, …). The server NEVER ships a
 * display string for these — it ships the structured payload and each
 * client renders the localized template, substituting `你` ("you") for
 * the viewer when they are the actor or one of the targets.
 *
 * Shared by the in-thread system bubble (ChatConversationScreen) and the
 * inbox preview line (the thread store's live reducer) so both read
 * identically. Mirrors the backend `previewFromPayload` /
 * `notificationPreview` wording — keep the two in sync.
 */

import {
  ChatNotificationPayload,
  ChatOrderInfoUpdatedPayload,
} from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { ReceptionOrderStatus } from '@euler/model/enum/ReceptionOrderStatus';
import { idEquals } from '@euler/utils';

/**
 * Localized preview/body text for a notification payload, viewer-aware.
 *
 *   inviter == me  → "你邀请<invitees>加入群聊"
 *   me ∈ invitees  → "<inviter>邀请你、<others>加入群聊"
 *   otherwise      → "<inviter>邀请<invitees>加入群聊"
 */
export function chatNotificationText(
  payload: Partial<ChatNotificationPayload> | null | undefined,
  myPlatformUserId: IdType,
): string {
  if (!payload) return '';
  if (payload.notificationType === 'members.invited') {
    const inviterIsMe =
      payload.inviterUserId != null &&
      idEquals(payload.inviterUserId, myPlatformUserId);
    // `||` (not `??`) so an empty-string name also falls back — matches
    // the server's `notificationPreview` so the inbox line and the
    // in-thread bubble render byte-identically.
    const inviter = inviterIsMe ? '你' : payload.inviterName || '某人';
    const invitees = payload.invitees ?? [];
    if (invitees.length === 0) {
      return `${inviter}邀请新成员加入群聊`;
    }
    // When the viewer is one of the invitees, render "你" FIRST, then the
    // rest in original order ("…邀请你、A、B…") — matches the server's
    // notificationPreview.
    let meIsInvitee = false;
    const others: string[] = [];
    for (const i of invitees) {
      if (
        i?.platformUserId != null &&
        idEquals(i.platformUserId, myPlatformUserId)
      ) {
        meIsInvitee = true;
      } else {
        others.push(i?.name || '某人');
      }
    }
    const names = meIsInvitee ? ['你', ...others] : others;
    return `${inviter}邀请${names.join('、')}加入群聊`;
  }
  if (payload.notificationType === 'room.dismissed') {
    const ownerIsMe =
      payload.ownerUserId != null &&
      idEquals(payload.ownerUserId, myPlatformUserId);
    const owner = ownerIsMe ? '你' : payload.ownerName || '群主';
    const title = payload.roomTitle?.trim();
    return title ? `${owner}已解散群${title}` : `${owner}已解散群聊`;
  }
  if (payload.notificationType === 'members.removed') {
    // The removed person reads "你已被移出群聊"; everyone else reads who the
    // owner removed. Matches the backend `notificationPreview`.
    const removed = payload.removed ?? [];
    const meRemoved = removed.some(
      r =>
        r?.platformUserId != null &&
        idEquals(r.platformUserId, myPlatformUserId),
    );
    if (meRemoved) return '你已被移出群聊';
    const removerIsMe =
      payload.removerUserId != null &&
      idEquals(payload.removerUserId, myPlatformUserId);
    const remover = removerIsMe ? '你' : payload.removerName || '群主';
    const names = removed.map(r => r?.name || '某人');
    if (names.length === 0) return `${remover}移除了群成员`;
    return `${remover}将${names.join('、')}移出群聊`;
  }
  if (payload.notificationType === 'title.changed') {
    const changerIsMe =
      payload.changerUserId != null &&
      idEquals(payload.changerUserId, myPlatformUserId);
    const changer = changerIsMe ? '你' : payload.changerName || '群主';
    const t = payload.title?.trim();
    return t ? `${changer}修改群名为“${t}”` : `${changer}恢复了默认群名`;
  }
  if (payload.notificationType === 'order.info_updated') {
    // Mirrors the backend `orderInfoChangeText` — status reads as an event
    // (工单已开始 / 已回退…), not "状态更新为 X". Not viewer-aware.
    return orderInfoChangeText(payload);
  }
  if (payload.notificationType === 'members.joined') {
    const joinerIsMe =
      payload.joinerUserId != null &&
      idEquals(payload.joinerUserId, myPlatformUserId);
    return joinerIsMe
      ? '你加入了群聊'
      : `${payload.joinerName || '某人'}加入了群聊`;
  }
  return payload._s ?? '[系统通知]';
}

/** One-line text for an order-info change (mirrors backend `orderInfoChangeText`). */
function orderInfoChangeText(p: Partial<ChatOrderInfoUpdatedPayload>): string {
  switch (p.field) {
    case 'licensePlateNo':
      return `车牌号更新为 ${p.value ?? '—'}`;
    case 'vehicleMileage':
      return `行驶里程更新为 ${p.value ?? '—'} km`;
    case 'customerName':
      return `客户更新为 ${p.value ?? '—'}`;
    case 'status':
      return orderStatusChangeText(p);
    default:
      return p._s ?? '[系统通知]';
  }
}

function orderStatusChangeText(
  p: Partial<ChatOrderInfoUpdatedPayload>,
): string {
  // A rollback also lands the order InProgress, but reads as a rollback.
  if (p.isRollback) {
    return p.rollbackReason
      ? `工单已回退（${p.rollbackReason}）`
      : '工单已回退';
  }
  switch (p.value) {
    case ReceptionOrderStatus.InProgress:
      return '工单已开始';
    case ReceptionOrderStatus.Cancelled:
      return '工单已取消';
    case ReceptionOrderStatus.Finished:
      return '工单已完成';
    case ReceptionOrderStatus.Pending:
      return '工单待处理';
    default:
      return p._s ?? '工单状态已更新';
  }
}

/**
 * Did the viewer themselves TRIGGER this system message — the inviter of
 * a `members.invited`, the owner of a `room.dismissed`? A system message
 * carries no actor on the WS envelope (author is "system"), so the inbox
 * reducer derives "is this mine?" from the structured payload to avoid
 * bumping the actor's own unread count. Accepts the raw payload (string
 * or object) straight off the envelope.
 */
export function isOwnSystemAction(
  rawPayload: unknown,
  myPlatformUserId: IdType,
): boolean {
  const p = parseLoose(rawPayload);
  if (!p) return false;
  const actorId =
    p.notificationType === 'members.invited'
      ? p.inviterUserId
      : p.notificationType === 'room.dismissed'
      ? p.ownerUserId
      : p.notificationType === 'members.removed'
      ? p.removerUserId
      : p.notificationType === 'title.changed'
      ? p.changerUserId
      : p.notificationType === 'members.joined'
      ? p.joinerUserId
      : null;
  return actorId != null && idEquals(actorId, myPlatformUserId);
}

function parseLoose(payload: unknown): any {
  if (payload == null) return null;
  if (typeof payload !== 'string') return payload;
  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
}
